import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { BookingDTO } from '@lkp/parking-sdk';
import { Subject, map, debounceTime, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import { FindBookings } from 'src/app/core/states/bookings/booking.actions';
import { BookingsState } from 'src/app/core/states/bookings/bookings.state';
import { BookingSearchListener } from 'src/app/core/states/bookings/booking.actions';
import { OrdersState } from 'src/app/core/states/orders/orders.state';

@Component({
    selector: 'app-side-menu-search',
    templateUrl: './side-menu-search.component.html',
    styleUrls: ['./side-menu-search.component.scss'],
    standalone: false
})
export class SideMenuSearchComponent {
  @Input() drawerActive = false;
  @Output() closeDrawer: EventEmitter<any> = new EventEmitter();
  @ViewChild('searchBox') searchBox!: ElementRef<HTMLInputElement>;
  searchResultUpcoming?: any;
  searchResultHistory?: any;
  searchResultUpcomingBase?: any[];
  searchResultHistoryBase?: any[];
  termNotFound?: string;
  searchInput = new FormControl<string | undefined>(undefined);
  showSpinner = false;
  termTooShort = false;
  activeOrder = false;
  upcomingSortOrder: 'asc' | 'desc' | undefined = undefined;
  upcomingPrevColumn: string | undefined = undefined;
  historySortOrder: 'asc' | 'desc' | undefined = undefined;
  historyPrevColumn: string | undefined = undefined;

  readonly onDestroy$ = new Subject<void>();

  constructor(readonly store: Store, actions$: Actions) {
    actions$
      .pipe(
        ofActionDispatched(BookingSearchListener),
        map((action: BookingSearchListener) => action.term),
        debounceTime(400),
        takeUntil(this.onDestroy$)
      )
      .subscribe((term) => {
        term &&
          this.store
            .dispatch(new FindBookings(term))
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
              this.showSpinner = false;
              if (!this.searchResultHistory && !this.searchResultUpcoming) {
                this.termNotFound = term;
              } else {
                this.termNotFound = undefined;
              }
            });
      });
  }

  ngOnInit() {
    this.store
      .select(OrdersState.order)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((order) => {
        this.activeOrder = !!order?.orderId;
      });

    /* Listen to search result in state */
    this.store
      .select(BookingsState.searchResultUpcoming)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result && result.length > 0) {
          if (this.searchInput.value) {
            this.searchResultUpcomingBase = result;
            this.applySort('upcoming');
          }
        } else {
          this.searchResultUpcomingBase = undefined;
          this.searchResultUpcoming = undefined;
        }
      });

    this.store
      .select(BookingsState.searchResultHistory)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result && result.length > 0) {
          if (this.searchInput.value) {
            this.searchResultHistoryBase = result;
            this.applySort('history');
          }
        } else {
          this.searchResultHistoryBase = undefined;
          this.searchResultHistory = undefined;
        }
      });
  }

  ngOnChanges(): void {
    this.store
      .select(OrdersState.order)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((order) => {
        this.activeOrder = !!order?.orderId;
      });
  }

  ngAfterViewChecked() {
    if (this.drawerActive) this.searchBox.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  search(term: string | undefined): void {
    this.searchResultUpcoming = undefined;
    this.searchResultHistory = undefined;
    this.searchResultUpcomingBase = undefined;
    this.searchResultHistoryBase = undefined;

    if (term === undefined || !term.trim().length) {
      /* Reset values */
      this.termNotFound = undefined;
      this.searchResultUpcoming = undefined;
      this.searchResultHistory = undefined;
      this.showSpinner = false;
      this.termTooShort = false;
      this.upcomingSortOrder = undefined;
      this.upcomingPrevColumn = undefined;
      this.historySortOrder = undefined;
      this.historyPrevColumn = undefined;
    } else if (term.length < 3) {
      this.termNotFound = undefined;
      this.termTooShort = true;
    } else {
      /* Search */
      this.termTooShort = false;
      this.showSpinner = true;
      this.store.dispatch(new BookingSearchListener(term));
    }
  }

  getTotalPassengers(bookings: BookingDTO[]): number {
    return bookings.reduce(
      (acc: number, booking: BookingDTO) => (booking.qtyPersons || 0) + acc,
      0
    );
  }

  close() {
    this.closeDrawer.emit();
  }

  private sortByDate(
    base: any[] | undefined,
    column: 'departureDate' | 'arrivalDate' | undefined,
    order: 'asc' | 'desc' | undefined
  ): any[] | undefined {
    if (!base) return undefined;
    if (!column || !order) return base.slice();
    const asc = order === 'asc';
    const getT = (v: any) => {
      const t = v ? dayjs(v).valueOf() : NaN;
      if (isNaN(t)) return asc ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      return t;
    };
    return base.slice().sort((a: any, b: any) => {
      const tA = getT(a[column]);
      const tB = getT(b[column]);
      return asc ? tA - tB : tB - tA;
    });
  }

  private applySort(kind: 'upcoming' | 'history') {
    if (kind === 'upcoming') {
      this.searchResultUpcoming = this.sortByDate(
        this.searchResultUpcomingBase,
        this.upcomingPrevColumn as 'departureDate' | 'arrivalDate' | undefined,
        this.upcomingSortOrder
      );
    } else {
      this.searchResultHistory = this.sortByDate(
        this.searchResultHistoryBase,
        this.historyPrevColumn as 'departureDate' | 'arrivalDate' | undefined,
        this.historySortOrder
      );
    }
  }

  onSort(
    kind: 'upcoming' | 'history',
    e: { column?: string; sort?: 'asc' | 'desc' }
  ) {
    if (kind === 'upcoming') {
      this.upcomingPrevColumn = e.column;
      this.upcomingSortOrder = e.sort;
    } else {
      this.historyPrevColumn = e.column;
      this.historySortOrder = e.sort;
    }
    this.applySort(kind);
  }
}
